/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { CircularProgress, Icon, Typography } from "./..";
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { detectColor, fade, hexToRGBA, hslToRgb, isColor, isColorName, rgbaToHexA, rgbToHex } from "../../styles/color-helper";
import * as allColors from "../../styles/colors";
import { alphaPseudo, bgColor, border, borderColor, borderNone, borderRadius4px, boxBorder, cursorNotAllowed, cursorPointer, displayFlex, displayInlineFlex, flexRow, itemsCenter, justifyCenter, mg, mgl, mgr, outlineNone, overflowHidden, parseHeight, parseMinWidth, parseMinWidthHeight, parseWidthHeight, pd, pointerEventsNone, positionRelative, textColor, userSelectNone, whiteSpaceNoWrap } from "../../styles/general";
import { useColor as colors, useTheme } from "../../theme";
import { classNames } from "../../utils";
import Ripple from "./ripple-effect";
const {
  spacing
} = useTheme();
export const getRippleColor = (color, viewType, colors) => {
  if (!isColor(color) && !isColorName(color)) {
    let level = 3;
    let name = color;
    if (name === 'primary') {
      name = 'formal';
    }
    const isSystemColor = colors[name + level];
    if (isSystemColor) {
      if (viewType === 'filled') {
        level = 7;
      }
      return colors[name + level];
    }
  }
  if (isColorName(color)) {
    return fade(isColorName(color), 0.5);
  }
  const colorType = detectColor(color);
  switch (colorType) {
    case 'hex':
    case 'hexa':
      return rgbaToHexA(fade(color, 0.5));
    case 'rgb':
      return rgbToHex(fade(color, 0.5));
    case 'rgba':
      return rgbaToHexA(fade(color, 0.5));
    case 'hsl':
      return rgbToHex(hslToRgb(fade(color, 0.5)));
    case 'hsla':
      return rgbaToHexA(hslToRgb(fade(color, 0.5)));
  }
};
const iconSizeMap = new Map([['tiny', spacing([4])], ['small', spacing([5])], ['medium', spacing([6])], ['large', spacing([8])], ['giant', spacing([10])]]);
const paddingSizeMap = new Map([['tiny', [1, 2]], ['small', [1.5, 3]], ['medium', [2, 4]], ['large', [2, 5]], ['giant', [2, 6]]]);
const outlinedPaddingSizeMap = new Map([['tiny', [0.75, 1.75]], ['small', [1.25, 2.75]], ['medium', [1.75, 3.75]], ['large', [1.75, 4.75]], ['giant', [1.75, 5.75]]]);
const iconMarginSizeMap = new Map([['tiny', [1]], ['small', [1]], ['medium', [1]], ['large', [1.5]], ['giant', [2]]]);
const typographySizeMap = new Map([['tiny', 'h5'], ['small', 'h5'], ['medium', 'h4'], ['large', 'h2'], ['giant', 'h1']]);
const minHeightSizeMap = new Map([['tiny', '24px'], ['small', '32px'], ['medium', '40px'], ['large', '48px'], ['giant', '56px']]);
const minHeightSizeLinkMap = new Map([['tiny', '16px'], ['small', '20px'], ['medium', '24px'], ['large', '32px'], ['giant', '40px']]);
const alphaLoading = 0.2;
const Button = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  color: colorProp,
  disabled,
  endIcon,
  href,
  id,
  label,
  labelProps,
  loading,
  onClick,
  size,
  startIcon,
  stopPropagation,
  text,
  type,
  urlParams,
  viewType,
  ...props
}, reference) => {
  if (!reference) reference = useRef(null);
  label = label || text || '';
  const ref = useRef(null);
  const rippleRef = useRef(null);
  const [urlState, setUrlState] = useState(href);
  const isColorDefault = colorProp === 'default';
  const isViewTypeLink = viewType === 'link';
  const iconSize = iconSizeMap.get(size);
  const paddingSize = isViewTypeLink ? 0 : viewType === 'outlined' ? outlinedPaddingSizeMap.get(size) : paddingSizeMap.get(size);
  const typographySize = typographySizeMap.get(size);
  const iconMarginSize = iconMarginSizeMap.get(size);
  const minHeightSize = isViewTypeLink ? minHeightSizeLinkMap.get(size) : minHeightSizeMap.get(size);
  const color = colors[colorProp] || colorProp;
  const TagName = href ? 'a' : 'button';
  const _ButtonRootCSS = ButtonRootCSS(color, isColorDefault, viewType, loading);
  const _ButtonIconCSS = ButtonIconCSS(endIcon, label, startIcon, iconMarginSize, iconSize);
  const _NonStartIconCSS = NonStartIconCSS(iconSize);
  const _ButtonLabelCSS = ButtonLabelCSS(loading, iconMarginSize);
  const _ButtonSizeCSS = ButtonSizeCSS(paddingSize, minHeightSize);
  const _onClick = e => {
    if (href) e.preventDefault(); //Prevent open link when click
    onClick && stopPropagation && e.stopPropagation();
    if (disabled || loading) return;
    if (!isViewTypeLink) rippleRef.current.start(e);
    onClick && onClick(e);
  };
  useEffect(() => {
    if (href) {
      const qs = urlParams ? '?' + new URLSearchParams(urlParams).toString() : '';
      const url = href + qs;
      setUrlState(url);
    }
  }, [href, urlParams]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const StartIcon = useMemo(() => {
    if (!startIcon) return jsx("span", {
      className: 'non-start-icon',
      css: _NonStartIconCSS
    });
    let node = startIcon;
    if (typeof node === 'string') {
      node = jsx(Icon, {
        name: node,
        width: iconSize,
        height: iconSize,
        color: 'currentColor',
        viewBox: true
      });
      return jsx("span", {
        css: _ButtonIconCSS,
        className: 'DGN-UI-Button-Start-Icon'
      }, node);
    }
    return jsx("span", {
      css: _ButtonIconCSS,
      className: 'DGN-UI-Button-Custom-Start-Icon'
    }, node);
  }, [startIcon, size]);
  const EndIcon = useMemo(() => {
    let node = endIcon;
    if (typeof node === 'string') {
      node = jsx(Icon, {
        name: node,
        width: iconSize,
        height: iconSize,
        color: 'currentColor',
        viewBox: true
      });
      return jsx("span", {
        css: _ButtonIconCSS,
        className: 'DGN-UI-Button-End-Icon'
      }, node);
    }
    return jsx("span", {
      css: _ButtonIconCSS,
      className: 'DGN-UI-Button-Custom-End-Icon'
    }, node);
  }, [endIcon, size]);
  const IconLoading = jsx(CircularProgress, {
    className: 'DGN-UI-Button-Loading',
    background: 'transparent',
    color: 'currentColor',
    size: parseInt(iconSize)
  });
  return useMemo(() => {
    // 1. Có StartIcon => thay StartIcon
    // 2. Có EndIcon => thay EndIcon
    // 3. Có StartIcon + EndIcon => thay StartIcon
    // 4. Không có Icon => hiện thêm icon loading ở đầu

    let start = StartIcon;
    let end = endIcon ? EndIcon : null;
    if (loading) {
      if (startIcon) start = IconLoading;
      if (endIcon) end = IconLoading;
      if (startIcon && endIcon) {
        start = IconLoading;
        end = EndIcon;
      }
      if (!startIcon && !endIcon) start = IconLoading;
    }
    return jsx(TagName, {
      css: [_ButtonRootCSS, _ButtonSizeCSS],
      ref: ref,
      onClick: _onClick,
      id: id,
      type: type,
      disabled: disabled,
      className: classNames('DGN-UI-Button', viewType, size, className),
      href: urlState || null,
      ...props
    }, start, jsx(Typography, {
      css: _ButtonLabelCSS,
      type: typographySize,
      className: classNames('DGN-UI-Button-Label', 'button-text'),
      format: ['uppercase'],
      ...labelProps
    }, children || label), end, !disabled && !loading && !isViewTypeLink ? jsx(Ripple, {
      ref: rippleRef,
      color: getRippleColor(colorProp, viewType, allColors)
    }) : null);
  }, [children, className, colorProp, disabled, endIcon, href, id, label, labelProps, loading, onClick, size, startIcon, stopPropagation, type, urlParams, viewType, urlState]);
}));
const NonStartIconCSS = iconSize => css`
    ${mg(0)};
    ${parseHeight(iconSize)};
    ${parseMinWidth(0)};
`;
const ButtonIconCSS = (endIcon, label, startIcon, iconMarginSize, iconSize) => css`
    ${displayFlex};
    ${flexRow};
    ${itemsCenter};
    ${justifyCenter};
    ${positionRelative};
    ${boxBorder};
    ${mgr(endIcon || label ? iconMarginSize : 0)};
    &.DGN-UI-Button-End-Icon,
    &.DGN-UI-Button-Custom-End-Icon {
        ${mgr('unset')};
        ${mgl(startIcon || label ? iconMarginSize : 0)};
    }
    &.DGN-UI-Button-Start-Icon,
    &.DGN-UI-Button-End-Icon {
        ${parseWidthHeight(iconSize)};
    }
`;
const ButtonLabelCSS = (loading, iconMarginSize) => css`
    ${whiteSpaceNoWrap};
    ${textColor('inherit')};
    ${mgl(loading ? iconMarginSize : 0)};
`;
const ButtonSizeCSS = (paddingSize, minHeightSize) => css`
    ${pd(paddingSize)};
    ${parseMinWidthHeight('max-content', minHeightSize)};
`;
const ButtonRootCSS = (color, isColorDefault, viewType, loading) => css`
    ${displayInlineFlex};
    ${itemsCenter};
    ${justifyCenter};
    ${positionRelative};
    ${borderRadius4px};
    ${boxBorder};
    ${cursorPointer};
    ${overflowHidden};
    ${outlineNone};
    ${userSelectNone};
    ${parseHeight('max-content')};
    text-decoration: none;
    transition: all 0.5s;
    ${loading && pointerEventsNone};
    ${viewType === 'outlined' && css`
        ${textColor(color)};
        ${border(1, color)};
        ${bgColor('transparent')};
        ${loading && bgColor(hexToRGBA(color, alphaLoading))};
        ${Object.keys(alphaPseudo).map(key => css`
                    &: ${key} {
                        ${bgColor(hexToRGBA(color, alphaPseudo[key]))};
                    }
                `)}
        ${isColorDefault && css`
            ${border(1, 'system/rest')};
            ${textColor('system/rest')};
            ${loading && css`
                ${textColor('system/active')};
                ${borderColor('system/active')};
                ${bgColor(hexToRGBA('system/active', alphaLoading))};
            `}
            ${Object.keys(alphaPseudo).map(key => css`
                        &: ${key} {
                            ${textColor('system/active')};
                            ${borderColor('system/active')};
                            ${bgColor(hexToRGBA('system/active', alphaPseudo[key]))};
                        }
                    `)} // &:hover | &:focus | &:active
        `}
        :disabled {
            ${borderColor('system/disabled')};
            ${bgColor('transparent')};
        }
    `}
    ${viewType === 'filled' && css`
        ${borderNone};
        ${textColor('system/white')};
        ${bgColor(color)};
        font-weight: bold;
        ${loading && css`
            filter: brightness(0.85);
        `}
        &:hover,
        &:focus {
            filter: brightness(0.85);
        }
        &:active {
            filter: brightness(0.7);
        }
        ${isColorDefault && css`
            ${bgColor('system/rest')};
            ${loading && bgColor('system/active')};
            &:hover,
            &:focus,
            &:active {
                ${bgColor('system/active')};
            }
        `}
        :disabled {
            ${bgColor('fill/disabled')};
            filter: brightness(1);
        }
    `}
    ${viewType === 'text' && css`
        ${bgColor('transparent')};
        ${borderNone};
        ${textColor(color)};
        ${loading && bgColor(hexToRGBA(color, alphaLoading))};
        ${Object.keys(alphaPseudo).map(key => css`
                    &: ${key} {
                        ${bgColor(hexToRGBA(color, alphaPseudo[key]))};
                    }
                `)}
        ${isColorDefault && css`
            ${textColor('system/rest')};
            ${loading && css`
                ${textColor('system/active')};
                ${bgColor(hexToRGBA('system/active', alphaLoading))};
            `}
            ${Object.keys(alphaPseudo).map(key => css`
                        &: ${key} {
                            ${textColor('system/active')};
                            ${bgColor(hexToRGBA('system/active', alphaPseudo[key]))};
                        }
                    `)}
        `}
        :disabled {
            ${bgColor('transparent')};
        }
    `}
    ${viewType === 'link' && css`
        ${borderNone};
        ${textColor(isColorDefault ? 'semantic/info' : color)};
        ${bgColor('transparent')};
        ${loading && css`
            filter: brightness(0.85);
        `}
        &:hover,
        &:focus {
            filter: brightness(0.85);
        }
        &:active {
            filter: brightness(0.7);
        }
        :disabled {
            filter: brightness(1);
        }
    `}
    :disabled {
        ${cursorNotAllowed};
        ${textColor('system/disabled')};
    }
`;
Button.defaultProps = {
  className: '',
  color: 'default',
  disabled: false,
  label: '',
  loading: false,
  size: 'medium',
  stopPropagation: true,
  viewType: 'text'
};
Button.propTypes = {
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** The color of the component. */
  color: PropTypes.string,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** [Icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic--basic) or element placed after the children. */
  endIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** The URL to link to when the button is clicked. If defined, an `a` element will be used as the root node. */
  href: PropTypes.string,
  /** The content of the component. */
  label: PropTypes.string,
  /** [Props](https://core.diginet.com.vn/ui/?path=/docs/components-typography--title) applied to the `Typography` label. */
  labelProps: PropTypes.object,
  /** If `true`, the loading indicator is shown. */
  loading: PropTypes.bool,
  /** Callback fired when the component is clicked. */
  onClick: PropTypes.func,
  /** The size of the component. */
  size: PropTypes.oneOf(['tiny', 'small', 'medium', 'large', 'giant']),
  /** [Icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic--basic) or element placed before the children. */
  startIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Query params pass to url. */
  urlParams: PropTypes.object,
  /** The variant to use. */
  viewType: PropTypes.oneOf(['text', 'outlined', 'filled', 'link'])
  /**
   * ref methods (ref.current.instance.*method*)
   *
   * * option(): Gets all UI component properties
   *  * Returns value - object
   * * option(optionName): Gets the value of a single property
   *  * @param {optionName} - string
   *  * Returns value - any
   * * option(optionName, optionValue): Updates the value of a single property
   *  * @param {optionName} - string
   *  * @param {optionValue} - any
   * * option(options): Updates the values of several properties
   *  * @param {options} - object
   */
};

export { Button };
export default OptionWrapper(Button);